import { getFlag } from '@rsos/feature-flags';
import isValidCoordinates from '@rsos/utils/isValidCoordinates';

/** Datadog flag. Off hides address-only ALI calls and the unplotted-call UI. */
export const UNGEOCODED_CALLS_FLAG = 'ungeocoded-calls';

type CivicLocation = {
  address_single_line?: string | null;
  latitude?: number | string | null;
  longitude?: number | string | null;
} | null;

/** ALI single-line address, trimmed. Empty when the payload has none. */
export const getCivicAddress = (location?: CivicLocation): string => {
  const address = location?.address_single_line;
  return typeof address === 'string' ? address.trim() : '';
};

const isNumericZero = (value: unknown): boolean =>
  (typeof value === 'number' ||
    (typeof value === 'string' && value.trim() !== '')) &&
  Number(value) === 0;

/** 0, 0 is a placeholder, not a place to plot. */
export const isNullIsland = (location?: CivicLocation): boolean =>
  !!location &&
  isNumericZero(location.latitude) &&
  isNumericZero(location.longitude);

/** Coordinates the map should draw. Excludes null island. */
export const isPlottablePoint = (location?: CivicLocation): boolean =>
  isValidCoordinates(location) && !isNullIsland(location);

/**
 * The call has an address and no coordinates the map can plot.
 * 0, 0 counts as no coordinates. Plotted calls keep their pin.
 */
export const isUnplottableAddress = (location?: CivicLocation): boolean =>
  getCivicAddress(location).length > 0 && !isPlottablePoint(location);

/**
 * A websocket location body can enter the call queue when it has
 * coordinates, or when it has a civic address and nothing to plot.
 */
export const hasQueueableLocation = (body?: CivicLocation): boolean => {
  if (!body) return false;
  if (isPlottablePoint(body)) return true;
  return (
    getFlag(UNGEOCODED_CALLS_FLAG, false) && getCivicAddress(body).length > 0
  );
};

type CallWithLocations = {
  core?: {
    locations?: CivicLocation[];
    secondaryLocations?: CivicLocation[];
  };
} | null;

/** A call has at least one coordinate the map can plot. */
export const callHasPlottableLocation = (call?: CallWithLocations): boolean => {
  const locations = [
    ...(call?.core?.locations || []),
    ...(call?.core?.secondaryLocations || []),
  ];
  return locations.some(location => isPlottablePoint(location));
};

/**
 * When `ungeocodedCallsEnabled` is false, drop calls that only have an
 * ungeocoded address. Text-only queue entries have no location list and stay.
 */
export const excludeUngeocodedCalls = <T extends Record<string, unknown>>(
  callQueue: T,
  ungeocodedCallsEnabled: boolean,
): T => {
  if (ungeocodedCallsEnabled) return callQueue;
  return Object.fromEntries(
    Object.entries(callQueue).filter(([, call]) => {
      const locations = (call as CallWithLocations)?.core;
      if (!locations) return true;
      const count =
        (locations.locations?.length || 0) +
        (locations.secondaryLocations?.length || 0);
      if (!count) return true;
      return callHasPlottableLocation(call as CallWithLocations);
    }),
  ) as T;
};
